import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import AdminLayout from './AdminLayout';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faSearch, 
  faEdit, 
  faTrashAlt, 
  faEye, 
  faFilter,
  faDownload,
  faExclamationTriangle
} from '@fortawesome/free-solid-svg-icons';
import { ADMIN_URLS } from '../../components/config/apiConfig';

function MembersList() {
  const navigate = useNavigate();
  const [members, setMembers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [filter, setFilter] = useState({
    membershipType: '',
    status: '',
    city: '',
    state: '',
    pincode: ''
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalMembers, setTotalMembers] = useState(0);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [memberToDelete, setMemberToDelete] = useState(null);
  const [error, setError] = useState(null);
  const [sessionAlert, setSessionAlert] = useState(false);
  
  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);
    
    return () => clearTimeout(timer);
  }, [searchTerm]);
  
  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (!token) {
      handleAuthError();
      return;
    }
    
    fetchMembers();
  }, [currentPage, filter, debouncedSearchTerm]);
  
  // Handle authentication errors
  const handleAuthError = () => {
    localStorage.removeItem('adminToken'); // Clear invalid token
    setSessionAlert(true);
    
    // Redirect to login after showing alert briefly
    setTimeout(() => {
      navigate('/admin/login');
    }, 2000);
  };
  
  // Check if token is valid before making API requests
  const getValidToken = () => {
    const token = localStorage.getItem('adminToken');
    if (!token) {
      return null;
    }
    
    // Basic validation - check if token is potentially valid JWT
    // A real implementation would validate expiration using JWT decode
    const tokenParts = token.split('.');
    if (tokenParts.length !== 3) {
      console.error('Invalid token format');
      handleAuthError();
      return null;
    }
    
    return token;
  };
  
  const fetchMembers = async () => {
    setIsLoading(true);
    setError(null);
    
    const token = getValidToken();
    if (!token) {
      setIsLoading(false);
      return;
    }
      
    try {
      let url = `${ADMIN_URLS.MEMBERS}?page=${currentPage}&perPage=10`;
      
      if (filter.membershipType) {
        url += `&membershipType=${filter.membershipType}`;
      }
      
      if (filter.status) {
        url += `&status=${filter.status}`;
      }
      
      if (filter.city.trim()) {
        url += `&city=${encodeURIComponent(filter.city.trim())}`;
      }
      
      if (filter.state.trim()) {
        url += `&state=${encodeURIComponent(filter.state.trim())}`;
      }
      
      if (filter.pincode.trim()) {
        url += `&pincode=${encodeURIComponent(filter.pincode.trim())}`;
      }
      
      if (debouncedSearchTerm.trim()) {
        url += `&search=${encodeURIComponent(debouncedSearchTerm.trim())}`;
      }
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.status === 401 || response.status === 403) {
        console.error('Authentication error:', response.status);
        handleAuthError();
        return;
      }
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `Failed to fetch members (${response.status})`);
      }
      
      const data = await response.json();
      setMembers(data.members);
      setTotalPages(data.totalPages);
      setCurrentPage(data.currentPage);
      setTotalMembers(data.totalMembers);
    } catch (error) {
      console.error('Error fetching members:', error);
      setError(error.message);
      setMembers([]);
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    setDebouncedSearchTerm(searchTerm);
  };
  
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilter(prev => ({
      ...prev,
      [name]: value
    }));
    setCurrentPage(1);
  };
  
  // Add a function to clear all filters
  const clearFilters = () => {
    setFilter({
      membershipType: '',
      status: '',
      city: '',
      state: '',
      pincode: ''
    });
    setSearchTerm('');
    setCurrentPage(1);
  };
  
  const handleDelete = (member) => {
    setMemberToDelete(member);
    setShowDeleteModal(true);
  };
  
  const confirmDelete = async () => {
    const token = getValidToken();
    if (!token) return;
    
    try {
      const response = await fetch(ADMIN_URLS.MEMBER_DETAIL(memberToDelete.id), {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      // Handle authentication errors
      if (response.status === 401 || response.status === 403) {
        const errorData = await response.json().catch(() => ({}));
        console.error('Auth error during delete:', errorData);
        
        // Only show session expired if it's actually a token issue
        if (errorData.code === 'TOKEN_EXPIRED' || 
            errorData.error?.includes('expired') || 
            errorData.error?.includes('token')) {
          handleAuthError();
          return;
        }
        
        // Otherwise show the specific error
        alert(`Authorization error: ${errorData.error || 'Access denied'}`);
        setShowDeleteModal(false);
        return;
      }
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete member');
      }
      
      // Remove member from state
      setMembers(members.filter(member => member.id !== memberToDelete.id));
      setTotalMembers(prev => prev - 1);
      setShowDeleteModal(false);
      setMemberToDelete(null);
      
      // If we deleted the last member on the page, go back a page (unless we're on page 1)
      if (members.length === 1 && currentPage > 1) {
        setCurrentPage(prev => prev - 1);
      } else {
        // Otherwise just refresh the current page
        fetchMembers();
      }
    } catch (error) {
      console.error('Error deleting member:', error);
      alert(`Failed to delete member: ${error.message}`);
      setShowDeleteModal(false);
    }
  };
  
  const cancelDelete = () => {
    setShowDeleteModal(false);
    setMemberToDelete(null);
  };
  
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };
  
  const getMembershipTypeLabel = (type) => {
    const types = {
      'primary_member': 'Primary',
      'associate_member': 'Associate',
      'student_membership': 'Student',
      'financial_collegium': 'Financial Collegium',
      'academic_collegium': 'Academic Collegium',
      'institutional_collegium': 'Institutional Collegium'
    };
    return types[type] || type;
  };
  
  const getStatusBadgeStyle = (status) => {
    const styles = {
      'active': { bg: '#d4edda', color: '#155724' },
      'pending': { bg: '#fff3cd', color: '#856404' },
      'inactive': { bg: '#f8d7da', color: '#721c24' }
    };
    return styles[status] || { bg: '#e2e3e5', color: '#383d41' };
  };
  
  // Helper to format address or show N/A
  const formatAddress = (member) => {
    if (!member.address) return 'N/A';
    const addressParts = [];
    if (member.address) addressParts.push(member.address);
    if (member.city) addressParts.push(member.city);
    if (member.state) addressParts.push(member.state);
    if (member.pincode) addressParts.push(member.pincode);
    
    return addressParts.length > 0 ? addressParts.join(', ') : 'N/A';
  };

  // Format or return N/A for phone
  const formatPhone = (phone) => {
    return phone || 'N/A';
  };
  
  // Format membership validity
  const formatMembershipValidity = (validUntil) => {
    if (!validUntil) return 'N/A';
    
    const validDate = new Date(validUntil);
    const today = new Date();
    
    // Determine if membership is expired
    const isExpired = validDate < today;
    
    // Format the date
    const formattedDate = validDate.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
    
    // Return formatted date with expiration indicator if needed
    return isExpired 
      ? <span style={{ color: 'var(--admin-danger)' }}>{formattedDate} (Inactive)</span>
      : formattedDate;
  };
  
  const exportMembersCSV = async () => {
    const token = getValidToken();
    if (!token) return;
    
    try {
      let url = `${ADMIN_URLS.MEMBERS}/export`;
      
      // Add any active filters
      const queryParams = [];
      if (filter.membershipType) queryParams.push(`membershipType=${filter.membershipType}`);
      if (filter.status) queryParams.push(`status=${filter.status}`);
      if (filter.city) queryParams.push(`city=${encodeURIComponent(filter.city)}`);
      if (filter.state) queryParams.push(`state=${encodeURIComponent(filter.state)}`);
      if (filter.pincode) queryParams.push(`pincode=${encodeURIComponent(filter.pincode)}`);
      if (searchTerm) queryParams.push(`search=${encodeURIComponent(searchTerm)}`);
      
      if (queryParams.length) {
        url += `?${queryParams.join('&')}`;
      }
      
      // Use fetch with proper Authorization header instead of form submission
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.status === 401 || response.status === 403) {
        handleAuthError();
        return;
      }
      
      if (!response.ok) {
        throw new Error(`Failed to export members (${response.status})`);
      }
      
      // Get the blob from the response
      const blob = await response.blob();
      
      // Create a URL for the blob
      const downloadUrl = window.URL.createObjectURL(blob);
      
      // Create a temporary link element to trigger the download
      const a = document.createElement('a');
      a.href = downloadUrl;
      
      // Generate filename based on filters
      const generateFilename = () => {
        const today = new Date();
        const dateStr = today.toISOString().split('T')[0]; // YYYY-MM-DD format
        
        let filename = 'bva_members';
        
        // Add filter descriptors to filename
        const descriptors = [];
        
        if (filter.membershipType) {
          descriptors.push(filter.membershipType);
        }
        
        if (filter.status) {
          descriptors.push(filter.status);
        }
        
        if (filter.city) {
          descriptors.push(filter.city.toLowerCase().replace(/\s+/g, '_'));
        }
        
        if (filter.state) {
          descriptors.push(filter.state.toLowerCase().replace(/\s+/g, '_'));
        }
        
        if (filter.pincode) {
          descriptors.push(`pin_${filter.pincode}`);
        }
        
        if (searchTerm) {
          descriptors.push(`search_${searchTerm.toLowerCase().replace(/\s+/g, '_')}`);
        }
        
        // Add descriptors to filename if any filters are active
        if (descriptors.length > 0) {
          filename += '_' + descriptors.join('_');
        }
        
        // Add date and extension
        filename += `_${dateStr}.csv`;
        
        return filename;
      };
      
      // Get filename from Content-Disposition header if available, otherwise generate one
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = generateFilename();
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?([^"]*)"?/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1];
        }
      }
      
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      
      // Clean up
      window.URL.revokeObjectURL(downloadUrl);
      document.body.removeChild(a);
      
    } catch (error) {
      console.error('Error exporting members:', error);
      alert('Failed to export members data');
    }
  };

  return (
    <AdminLayout>
      {sessionAlert && (
        <div style={{
          position: 'fixed',
          top: '1rem',
          left: '50%',
          transform: 'translateX(-50%)',
          backgroundColor: '#fff3cd',
          color: '#856404',
          padding: '1rem',
          borderRadius: '4px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          zIndex: 1100,
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          maxWidth: '90%',
          width: '400px'
        }}>
          <FontAwesomeIcon icon={faExclamationTriangle} />
          Your session has expired. Redirecting to login...
        </div>
      )}
      
      <div className="admin-page-header">
        <h1 className="admin-page-title">Members</h1>
        <ul className="admin-breadcrumb">
          <li className="admin-breadcrumb-item">
            <Link to="/admin/dashboard" className="admin-breadcrumb-link">Dashboard</Link>
          </li>
          <li className="admin-breadcrumb-item active">
            <span className="admin-breadcrumb-link">Members</span>
          </li>
        </ul>
      </div>
      
      {/* Compact Search and Filter Section */}
      <div className="admin-card" style={{ marginBottom: '1.5rem' }}>
        <div className="admin-card-body" style={{ padding: '1rem' }}>
          {/* Search and Quick Actions Row */}
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            flexWrap: 'wrap', 
            gap: '0.75rem',
            marginBottom: '1rem'
          }}>
            {/* Search Input */}
            <form onSubmit={handleSearch} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flex: '1', minWidth: '280px' }}>
              <div style={{ position: 'relative', flex: '1', maxWidth: '400px' }}>
                <input 
                  type="text" 
                  placeholder="Search members..." 
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{ 
                    width: '100%',
                    padding: '0.5rem 0.5rem 0.5rem 2rem', 
                    borderRadius: '4px', 
                    border: '1px solid #dee2e6',
                    fontSize: '0.875rem',
                    transition: 'border-color 0.2s',
                    outline: 'none'
                  }}
                  onFocus={(e) => e.target.style.borderColor = 'var(--admin-primary)'}
                  onBlur={(e) => e.target.style.borderColor = '#dee2e6'}
                />
                <FontAwesomeIcon 
                  icon={faSearch} 
                  style={{ 
                    position: 'absolute', 
                    left: '0.5rem', 
                    top: '50%', 
                    transform: 'translateY(-50%)',
                    color: '#6c757d',
                    fontSize: '0.875rem'
                  }}
                />
              </div>
              <button 
                type="submit"
                className="admin-btn admin-btn-primary"
                style={{ 
                  padding: '0.5rem 1rem',
                  borderRadius: '4px',
                  fontSize: '0.875rem'
                }}
              >
                Search
              </button>
            </form>
            
            {/* Action Buttons */}
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <Link
                to="/admin/members/add"
                className="admin-btn admin-btn-primary"
                style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '0.5rem', 
                  padding: '0.5rem 0.75rem',
                  borderRadius: '4px',
                  fontSize: '0.875rem',
                  textDecoration: 'none'
                }}
              >
                + Add Member
              </Link>

              <button 
                onClick={clearFilters}
                className="admin-btn admin-btn-outline-secondary" 
                style={{ 
                  padding: '0.5rem 0.75rem',
                  borderRadius: '4px',
                  fontSize: '0.875rem',
                  border: '1px solid #6c757d',
                  backgroundColor: 'transparent',
                  color: '#6c757d'
                }}
              >
                Clear
              </button>
              
              <button 
                onClick={exportMembersCSV}
                className="admin-btn admin-btn-success" 
                style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '0.5rem', 
                  padding: '0.5rem 0.75rem',
                  borderRadius: '4px',
                  fontSize: '0.875rem',
                  backgroundColor: '#28a745',
                  border: '1px solid #28a745'
                }}
              >
                <FontAwesomeIcon icon={faDownload} />
                Export
              </button>
            </div>
          </div>
          
          {/* Compact Filters Row */}
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', 
            gap: '0.75rem',
            alignItems: 'end'
          }}>
            <div>
              <label style={{ 
                display: 'block', 
                marginBottom: '0.25rem', 
                fontSize: '0.75rem', 
                fontWeight: '500',
                color: '#6c757d',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>
                Type
              </label>
              <select
                name="membershipType"
                value={filter.membershipType}
                onChange={handleFilterChange}
                style={{ 
                  width: '100%',
                  padding: '0.5rem', 
                  borderRadius: '4px', 
                  border: '1px solid #dee2e6',
                  fontSize: '0.875rem',
                  backgroundColor: 'white',
                  cursor: 'pointer'
                }}
              >
                <option value="">All Types</option>
                <option value="primary_member">Primary</option>
                <option value="associate_member">Associate</option>
                <option value="student_membership">Student</option>
                <option value="financial_collegium">Financial</option>
                <option value="academic_collegium">Academic</option>
                <option value="institutional_collegium">Institutional</option>
              </select>
            </div>
            
            <div>
              <label style={{ 
                display: 'block', 
                marginBottom: '0.25rem', 
                fontSize: '0.75rem', 
                fontWeight: '500',
                color: '#6c757d',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>
                Status
              </label>
              <select
                name="status"
                value={filter.status}
                onChange={handleFilterChange}
                style={{ 
                  width: '100%',
                  padding: '0.5rem', 
                  borderRadius: '4px', 
                  border: '1px solid #dee2e6',
                  fontSize: '0.875rem',
                  backgroundColor: 'white',
                  cursor: 'pointer'
                }}
              >
                <option value="">All Status</option>
                <option value="active">Active</option>
                <option value="pending">Pending</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
            
            <div>
              <label style={{ 
                display: 'block', 
                marginBottom: '0.25rem', 
                fontSize: '0.75rem', 
                fontWeight: '500',
                color: '#6c757d',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>
                City
              </label>
              <input
                type="text"
                name="city"
                placeholder="City"
                value={filter.city}
                onChange={handleFilterChange}
                style={{ 
                  width: '100%',
                  padding: '0.5rem', 
                  borderRadius: '4px', 
                  border: '1px solid #dee2e6',
                  fontSize: '0.875rem'
                }}
              />
            </div>
            
            <div>
              <label style={{ 
                display: 'block', 
                marginBottom: '0.25rem', 
                fontSize: '0.75rem', 
                fontWeight: '500',
                color: '#6c757d',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>
                State
              </label>
              <input
                type="text"
                name="state"
                placeholder="State"
                value={filter.state}
                onChange={handleFilterChange}
                style={{ 
                  width: '100%',
                  padding: '0.5rem', 
                  borderRadius: '4px', 
                  border: '1px solid #dee2e6',
                  fontSize: '0.875rem'
                }}
              />
            </div>
            
            <div>
              <label style={{ 
                display: 'block', 
                marginBottom: '0.25rem', 
                fontSize: '0.75rem', 
                fontWeight: '500',
                color: '#6c757d',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>
                Pincode
              </label>
              <input
                type="text"
                name="pincode"
                placeholder="Pincode"
                value={filter.pincode}
                onChange={handleFilterChange}
                style={{ 
                  width: '100%',
                  padding: '0.5rem', 
                  borderRadius: '4px', 
                  border: '1px solid #dee2e6',
                  fontSize: '0.875rem'
                }}
              />
            </div>
            
            {/* Active Filters Indicator */}
            {(Object.values(filter).some(v => v) || debouncedSearchTerm) && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '0.5rem',
                backgroundColor: '#e3f2fd',
                color: '#1565c0',
                borderRadius: '4px',
                fontSize: '0.75rem',
                fontWeight: '600',
                textAlign: 'center',
                minHeight: '36px'
              }}>
                {Object.values(filter).filter(v => v).length + (debouncedSearchTerm ? 1 : 0)} Active
              </div>
            )}
          </div>
        </div>
      </div>
      
      <div className="admin-card">
        <div className="admin-card-body p-0">
          {isLoading ? (
            <div style={{ padding: '2rem', textAlign: 'center' }}>Loading members data...</div>
          ) : error ? (
            <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--admin-danger)' }}>
              Error: {error}
            </div>
          ) : (
            <>
              {/* Desktop/Tablet View */}
              <div className="desktop-table-view">
                <div className="admin-table-responsive" style={{ 
                  maxHeight: '70vh',
                  overflow: 'auto',
                  position: 'relative',
                  width: '100%',
                  boxSizing: 'border-box'
                }}>
                  <table className="admin-table" style={{ 
                    width: '100%',
                    tableLayout: 'fixed'
                  }}>
                    <thead style={{ 
                      position: 'sticky', 
                      top: 0, 
                      zIndex: 10,
                      backgroundColor: '#f8f9fa',
                      boxShadow: '0 2px 2px -1px rgba(0,0,0,0.1)'
                    }}>
                      <tr>
                        <th style={{ width: '8%' }}>ID</th>
                        <th style={{ width: '20%' }}>Name</th>
                        <th style={{ width: '25%' }}>Email</th>
                        <th style={{ width: '12%' }}>Phone</th>
                        <th style={{ width: '10%' }}>City</th>
                        <th style={{ width: '15%' }}>Membership</th>
                        <th style={{ width: '10%' }}>Status</th>
                        <th style={{ width: '10%' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {members.length > 0 ? (
                        members.map((member) => (
                          <tr key={member.id}>
                            <td>{member.membershipNumber}</td>
                            <td style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {member.firstName} {member.lastName}
                            </td>
                            <td style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {member.email}
                            </td>
                            <td>{formatPhone(member.phone)}</td>
                            <td>{member.city}</td>
                            <td style={{ textTransform: 'capitalize', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {getMembershipTypeLabel(member.membershipType)}
                            </td>
                            <td>
                              <span 
                                className="status-badge"
                                style={{
                                  padding: '0.25rem 0.5rem',
                                  borderRadius: '4px',
                                  fontSize: '0.875rem',
                                  backgroundColor: getStatusBadgeStyle(member.status).bg,
                                  color: getStatusBadgeStyle(member.status).color,
                                  textTransform: 'capitalize',
                                  display: 'inline-block',
                                  maxWidth: '100%',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis'
                                }}
                              >
                                {member.status}
                              </span>
                            </td>
                            <td style={{ 
                              position: 'sticky', 
                              right: 0, 
                              backgroundColor: 'white',
                              boxShadow: '-2px 0 2px -1px rgba(0,0,0,0.1)',
                              padding: '0.5rem',
                              textAlign: 'center'
                            }}>
                              <div className="admin-table-actions" style={{
                                display: 'flex',
                                justifyContent: 'center',
                                gap: '0.25rem'
                              }}>
                                <Link 
                                  to={`/admin/members/${member.id}`} 
                                  className="admin-table-btn admin-table-btn-view"
                                  title="View member details"
                                >
                                  <FontAwesomeIcon icon={faEye} />
                                </Link>
                                <Link 
                                  to={`/admin/members/${member.id}/edit`} 
                                  className="admin-table-btn admin-table-btn-edit"
                                  title="Edit member"
                                >
                                  <FontAwesomeIcon icon={faEdit} />
                                </Link>
                                <button 
                                  className="admin-table-btn admin-table-btn-delete"
                                  title="Delete member"
                                  onClick={() => handleDelete(member)}
                                >
                                  <FontAwesomeIcon icon={faTrashAlt} />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="7" style={{ textAlign: 'center', padding: '2rem' }}>
                            No members found matching your criteria
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
              
              {/* Mobile View */}
              <div className="mobile-table-view">
                {members.length > 0 ? (
                  members.map((member) => (
                    <div key={member.id} className="mobile-member-card">
                      <div className="mobile-member-header">
                        <div>
                          <strong>{member.firstName} {member.lastName}</strong>
                          <div className="mobile-member-id">ID: {member.id}</div>
                        </div>
                        <span 
                          className="status-badge"
                          style={{
                            backgroundColor: getStatusBadgeStyle(member.status).bg,
                            color: getStatusBadgeStyle(member.status).color,
                          }}
                        >
                          {member.status}
                        </span>
                      </div>
                      
                      <div className="mobile-member-contact">
                        <div>{member.email}</div>
                        <div>{formatPhone(member.phone)}</div>
                        <div>{member.city || 'N/A'}, {member.state || 'N/A'} - {member.pincode || 'N/A'}</div>
                      </div>
                      
                      <div className="mobile-member-details">
                        <div>Profession: {member.profession || 'N/A'}</div>
                        <div>Designation: {member.designation || 'N/A'}</div>
                        <div>Membership: {getMembershipTypeLabel(member.membershipType)}</div>
                        <div>Validity: {member.expiryDate}</div>
                        <div>Joined: {formatDate(member.joinDate)}</div>
                        <div>Address: {formatAddress(member)}</div>
                      </div>
                      
                      <div className="mobile-member-actions">
                        <Link 
                          to={`/admin/members/${member.id}`} 
                          className="admin-btn admin-btn-sm admin-btn-info"
                        >
                          <FontAwesomeIcon icon={faEye} /> View
                        </Link>
                        <Link 
                          to={`/admin/members/${member.id}/edit`} 
                          className="admin-btn admin-btn-sm admin-btn-secondary"
                        >
                          <FontAwesomeIcon icon={faEdit} /> Edit
                        </Link>
                        <button 
                          className="admin-btn admin-btn-sm admin-btn-danger"
                          onClick={() => handleDelete(member)}
                        >
                          <FontAwesomeIcon icon={faTrashAlt} /> Delete
                        </button>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="no-data-message">
                    No members found matching your criteria
                  </div>
                )}
              </div>
              
              {/* Add responsive styles */}
              <style dangerouslySetInnerHTML={{__html: `
                .mobile-table-view {
                  display: none;
                }
                
                @media (max-width: 768px) {
                  .desktop-table-view {
                    display: none;
                  }
                  .mobile-table-view {
                    display: block;
                  }
                }
                
                .mobile-member-card {
                  padding: 1rem;
                  border-bottom: 1px solid #e9e9e9;
                  margin-bottom: 0.5rem;
                }
                
                .mobile-member-header {
                  display: flex;
                  justify-content: space-between;
                  align-items: center;
                  margin-bottom: 0.75rem;
                }
                
                .mobile-member-id {
                  font-size: 0.85rem;
                  color: #666;
                  margin-top: 0.25rem;
                }
                
                .mobile-member-contact {
                  font-size: 0.9rem;
                  margin-bottom: 0.5rem;
                }
                
                .mobile-member-details {
                  font-size: 0.85rem;
                  color: #555;
                  margin-bottom: 0.75rem;
                }
                
                .mobile-member-actions {
                  display: flex;
                  justify-content: flex-end;
                  gap: 0.5rem;
                  flex-wrap: wrap;
                }
                
                .status-badge {
                  padding: 0.25rem 0.5rem;
                  border-radius: 4px;
                  font-size: 0.75rem;
                  text-transform: capitalize;
                }
                
                .admin-btn-sm {
                  padding: 0.25rem 0.5rem;
                  font-size: 0.875rem;
                }
                
                .no-data-message {
                  text-align: center;
                  padding: 2rem 1rem;
                  color: #666;
                }
                
                /* Pagination Styles */
                .admin-pagination {
                  display: flex;
                  align-items: center;
                  gap: 0.25rem;
                }
                
                .admin-pagination-btn {
                  background-color: white;
                  border: 1px solid var(--admin-border);
                  color: #333;
                  padding: 0.375rem 0.75rem;
                  min-width: 2.5rem;
                  text-align: center;
                  cursor: pointer;
                  border-radius: 4px;
                  font-size: 0.875rem;
                  transition: all 0.2s;
                }
                
                .admin-pagination-btn:hover:not(:disabled) {
                  background-color: #f0f0f0;
                }
                
                .admin-pagination-btn.active {
                  background-color: var(--admin-primary);
                  color: white;
                  border-color: var(--admin-primary);
                }
                
                .admin-pagination-btn:disabled {
                  opacity: 0.5;
                  cursor: not-allowed;
                }
                
                .admin-pagination-ellipsis {
                  padding: 0.375rem 0.5rem;
                  color: #666;
                }
                
                @media (max-width: 576px) {
                  .admin-pagination {
                    width: 100%;
                    justify-content: center;
                  }
                }
              `}} />
              
              <div style={{ 
                padding: '1rem',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: '1rem',
                borderTop: '1px solid var(--admin-border)'
              }}>
                <div>
                  Showing {members.length} of {totalMembers} members
                </div>
                
                <div className="admin-pagination">
                  <button 
                    className="admin-pagination-btn" 
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                  >
                    &laquo; Previous
                  </button>
                  
                  {/* Generate page buttons */}
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    // Logic to determine which page numbers to show
                    let pageNum;
                    if (totalPages <= 5) {
                      // Show all pages if 5 or fewer
                      pageNum = i + 1;
                    } else {
                      // Otherwise show a window of pages around the current page
                      if (currentPage <= 3) {
                        // Near the start
                        pageNum = i + 1;
                      } else if (currentPage >= totalPages - 2) {
                        // Near the end
                        pageNum = totalPages - 4 + i;
                      } else {
                        // In the middle
                        pageNum = currentPage - 2 + i;
                      }
                    }
                    
                    return (
                      <button 
                        key={pageNum}
                        className={`admin-pagination-btn ${currentPage === pageNum ? 'active' : ''}`}
                        onClick={() => setCurrentPage(pageNum)}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                  
                  {/* Show ellipsis if there are more pages */}
                  {totalPages > 5 && currentPage < totalPages - 2 && (
                    <span className="admin-pagination-ellipsis">...</span>
                  )}
                  
                  {/* Show last page if not in view */}
                  {totalPages > 5 && currentPage < totalPages - 2 && (
                    <button 
                      className="admin-pagination-btn"
                      onClick={() => setCurrentPage(totalPages)}
                    >
                      {totalPages}
                    </button>
                  )}
                  
                  <button 
                    className="admin-pagination-btn" 
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages || totalPages === 0}
                  >
                    Next &raquo;
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
      
      {showDeleteModal && (
        <div className="admin-modal" style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1050
        }}>
          <div className="admin-modal-content" style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '2rem',
            width: '100%',
            maxWidth: '500px',
            boxShadow: 'var(--admin-shadow)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '1rem', color: '#dc3545' }}>
              <FontAwesomeIcon icon={faExclamationTriangle} style={{ fontSize: '24px', marginRight: '12px' }} />
              <h2 style={{ margin: 0, color: '#dc3545' }}>Confirm Deletion</h2>
            </div>
            
            <div style={{ 
              backgroundColor: '#fff3cd', 
              border: '1px solid #ffc107',
              borderRadius: '4px',
              padding: '1rem',
              marginBottom: '1rem'
            }}>
              <p style={{ margin: '0 0 0.5rem 0', fontWeight: 'bold', color: '#856404' }}>
                ⚠️ Warning: This action cannot be undone!
              </p>
              <p style={{ margin: 0, fontSize: '0.9rem', color: '#856404' }}>
                Deleting this member will permanently remove:
              </p>
            </div>

            <div style={{ 
              backgroundColor: '#f8f9fa',
              borderRadius: '4px',
              padding: '1rem',
              marginBottom: '1rem'
            }}>
              <p style={{ margin: '0 0 0.5rem 0', fontWeight: 'bold' }}>
                Member: <span style={{ color: '#dc3545' }}>{memberToDelete.firstName} {memberToDelete.lastName}</span>
              </p>
              <ul style={{ 
                margin: '0.5rem 0 0 0', 
                paddingLeft: '1.5rem',
                fontSize: '0.9rem',
                color: '#6c757d'
              }}>
                <li>All payment records</li>
                <li>All program registrations</li>
                <li>Professional listing (if exists)</li>
                <li>Member profile and history</li>
              </ul>
            </div>

            <p style={{ 
              marginBottom: '1.5rem', 
              fontSize: '0.95rem',
              color: '#495057',
              fontWeight: '500'
            }}>
              Are you absolutely sure you want to proceed?
            </p>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem' }}>
              <button 
                className="admin-btn admin-btn-light"
                onClick={cancelDelete}
                style={{ backgroundColor: '#f8f9fa', borderColor: '#dee2e6' }}
              >
                Cancel
              </button>
              <button 
                className="admin-btn admin-btn-danger"
                onClick={confirmDelete}
                style={{ 
                  backgroundColor: '#dc3545',
                  fontWeight: 'bold'
                }}
              >
                Yes, Delete Permanently
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}

export default MembersList;
